import { connect } from 'react-redux';
import { orderBy } from 'lodash';
import i18next from 'i18next';
import React, { useEffect, useRef, useState } from 'react';

import { getIsInCentralKitchenOnlyPage } from '@admin/utils/centralKitchen';

import ExportModalContent from '@lib/inpulse/ExportModal';

import { getClientInfo } from '@selectors/client';

import recipeService from '@services/recipe';

import { buildExportRecipeRows } from '../../utils/formatRecipesForExport';
import { exportRecipesByStore } from '../../utils/exportRecipesByStore';

const BATCH_SIZE = 50;

const sortComponentRows = (rows) =>
  orderBy(rows, ['name', 'associatedProductName', 'componentQuantity'], ['asc', 'asc', 'desc']);

export const ExportRecipeByStoreProgressModal = (props) => {
  const {
    params: {
      title: propsTitle,
      customParams,
      selectedStatus,
      selectedStores,
      selectedBrands,
      recipesIds: propsRecipesIds,
    },
    currency,
    client: { hasMultipleBrands, defaultChannelId, clientChannels },
    closeModal,
  } = props;

  const [title, setTitle] = useState(propsTitle || '');
  const [progress, setProgress] = useState(0);
  const [loading, setLoading] = useState(true);

  const [currentPage, setCurrentPage] = useState(1);
  const [allRecipesIds, setAllRecipesIds] = useState([]);
  const [recipesIds, setRecipesIds] = useState([]);
  const [detailedRecipesList, setDetailedRecipesList] = useState([]);
  const [detailedRecipesWithComponents, setDetailedRecipesWithComponents] = useState([]);
  const [fetchingPhase, setFetchingPhase] = useState('init');

  // Promise of the dedicated export-by-store endpoint (per-store resolved variant compositions +
  // store resolutions). Started as soon as the exported recipe ids are known so its server-side
  // computation overlaps with the main recipe detail fetching.
  const exportDataPromiseRef = useRef(null);
  const rawRecipeByIdRef = useRef(new Map());

  const isInCentralKitchenOnlyPage = getIsInCentralKitchenOnlyPage();

  const channelsForExport = isInCentralKitchenOnlyPage
    ? clientChannels.filter(({ id }) => id === defaultChannelId)
    : clientChannels;

  const startExportDataFetch = (recipeIds) => {
    const storeIds = (selectedStores || []).map(({ id }) => id);

    exportDataPromiseRef.current = recipeService.getStoreVariantCompositions(recipeIds, storeIds);
  };

  const exitModal = () => {
    setTitle('');
    setProgress(0);
    setLoading(false);
    setRecipesIds([]);
    setDetailedRecipesList([]);
    setDetailedRecipesWithComponents([]);

    closeModal();
  };

  const exportReady = () => {
    setTitle(i18next.t('GENERAL.EXPORT_SUCCESS'));
    setLoading(false);
  };

  const exportFailure = () => {
    setTitle(i18next.t('GENERAL.EXPORT_FAILURE'));
    setLoading(false);
  };

  const buildRecipeRows = (recipes) =>
    buildExportRecipeRows(recipes, hasMultipleBrands, channelsForExport, defaultChannelId);

  // Phase 1: either export the selected recipes directly or start fetching all recipe IDs by
  // filters. In selection mode the recipe ids are known, so the dedicated export call starts now.
  useEffect(() => {
    if (propsRecipesIds && propsRecipesIds.length) {
      startExportDataFetch(propsRecipesIds);

      setAllRecipesIds(propsRecipesIds);
      setRecipesIds(propsRecipesIds);
      setFetchingPhase('processing_details');

      return;
    }

    // Mirror ExportRecipeModal: only fetch all recipes by filters when both are present, so a call
    // without selectedStatus never silently defaults to active: { eq: false } (inactive only).
    if (customParams && selectedStatus) {
      setFetchingPhase('fetching_ids');
    }
  }, []);

  // Phase 2: fetch paginated recipe IDs
  const fetchNextBatch = async () => {
    try {
      const { data, count } = await recipeService.getPaginated(
        {
          orderBy: 'name',
          orderType: 'asc',
          page: currentPage,
          limit: BATCH_SIZE,
          search: '',
        },
        {
          ...(hasMultipleBrands && {
            brandIds: {
              inArray: (selectedBrands || []).map(({ id }) => id),
            },
          }),
          isKitchen: { eq: isInCentralKitchenOnlyPage },
          active: { eq: selectedStatus?.filterValue ?? false },
          quantity: customParams?.quantity,
          categoryId: customParams?.categoryId,
          parentsCount: customParams?.parentsCount,
          mappingsCount: customParams?.mappingsCount,
          unit: customParams?.unit,
        },
      );

      const batchIds = (data || []).map(({ id }) => id);
      const updatedAllIds = [...allRecipesIds, ...batchIds];

      setAllRecipesIds(updatedAllIds);

      const hasMorePages = data && data.length === BATCH_SIZE && updatedAllIds.length < count;

      if (hasMorePages) {
        setCurrentPage(currentPage + 1);

        return;
      }

      // All recipe ids known: start the dedicated export call so it overlaps with the details
      startExportDataFetch(updatedAllIds);

      setRecipesIds([...updatedAllIds]);
      setFetchingPhase('processing_details');
    } catch {
      exportFailure();
    }
  };

  useEffect(() => {
    if (fetchingPhase !== 'fetching_ids') {
      return;
    }

    fetchNextBatch();
  }, [currentPage, fetchingPhase]);

  // Phase 3: fetch main recipe details in batches (kept sequential to avoid overloading the API)
  const loadRecipesByBatch = async (remainingIds) => {
    if (!remainingIds.length) {
      setFetchingPhase('finalizing');

      return;
    }

    const batchIds = remainingIds.slice(0, BATCH_SIZE);

    try {
      const recipes = await recipeService.getAssociatedRecipesByIds(batchIds, true, true);

      recipes.forEach((recipe) => {
        rawRecipeByIdRef.current.set(recipe.recipe.id, recipe);
      });

      const { recipeRows, componentRows } = buildRecipeRows(recipes);

      if (!loading) {
        return;
      }

      const nextIds = remainingIds.filter((id) => !batchIds.includes(id));

      setDetailedRecipesWithComponents((prev) => [...prev, ...componentRows]);
      setDetailedRecipesList((prev) => [...prev, ...recipeRows]);
      setRecipesIds(nextIds);
    } catch {
      exportFailure();
    }
  };

  useEffect(() => {
    if (fetchingPhase !== 'processing_details') {
      return;
    }

    setProgress(allRecipesIds.length ? 100 - (recipesIds.length / allRecipesIds.length) * 100 : 0);

    loadRecipesByBatch([...recipesIds]);
  }, [recipesIds, fetchingPhase]);

  /**
   * Build, for every resolved composition returned by the dedicated export call, its recipe-level
   * row and component rows, keyed by combinationKey (a recipe resolved identically across stores
   * shares one entry). Reuses the main recipe metadata (name, price, unit…) and only swaps in the
   * resolved composition. Compositions with no rows are skipped so stores fall back to the main
   * recipe — matching the consumption resolution.
   */
  const buildVariantData = (compositions) => {
    const recipeRowByKey = {};
    const componentRowsByKey = {};

    compositions.forEach(({ combinationKey, recipeId, compositionSettings, allergens }) => {
      const rawRecipe = rawRecipeByIdRef.current.get(recipeId);

      if (!rawRecipe || !compositionSettings.length) {
        return;
      }

      const variantRecipe = { ...rawRecipe, compositionSettings, allergens };
      const { recipeRows, componentRows } = buildRecipeRows([variantRecipe]);

      recipeRowByKey[combinationKey] = recipeRows[0];
      componentRowsByKey[combinationKey] = sortComponentRows(componentRows);
    });

    return { recipeRowByKey, componentRowsByKey };
  };

  // Phase 4: await the overlapped export call, then generate the Excel file
  useEffect(() => {
    if (fetchingPhase !== 'finalizing') {
      return;
    }

    const finalize = async () => {
      try {
        const { storeResolutions, compositions } = exportDataPromiseRef.current
          ? await exportDataPromiseRef.current
          : { storeResolutions: [], compositions: [] };

        if (!loading) {
          return;
        }

        const variantData = buildVariantData(compositions);

        exportRecipesByStore(
          detailedRecipesList,
          sortComponentRows(detailedRecipesWithComponents),
          currency,
          channelsForExport,
          hasMultipleBrands,
          selectedStores || [],
          storeResolutions,
          variantData,
        );

        exportReady();
      } catch {
        exportFailure();
      }
    };

    finalize();
  }, [fetchingPhase]);

  return (
    <ExportModalContent
      {...props}
      exitModal={exitModal}
      isLoading={loading}
      progress={progress}
      titleModal={title}
    />
  );
};

const mapStateToProps = (state) => ({
  currency: state.baseReducer.currency,
  client: getClientInfo(state.baseReducer.user),
});

export default connect(mapStateToProps)(ExportRecipeByStoreProgressModal);
